import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  ViewChild,
} from "@angular/core";
import { Location } from "@angular/common";
import { MatStepper } from "@angular/material/stepper";
import {
  DigLitSelfAssessmentAnswers,
  DigLitSelfAssessmentResult,
  DigLitSelfAssessmentStem,
} from "src/app/_models/DigitalLiteracy";
import { AppConstants } from "src/app/constants/constants";
import {
  DigLitQuestionCategories,
  DigLitSAConstants,
  generateGUID,
  getSliderAudio,
  QuestionModes,
  Voice,
} from "src/app/constants/DigitalLiteracyConstants";
import { Store } from "@ngrx/store";
import {
  selectDigLitCurrentStep,
  selectDigLitSelfAssessmentDataState,
  selectDigLitSelfAssessmentLoader,
  selectSaveDigLitSelfAssessmentError,
  selectSaveDigLitSelfAssessmentResult,
} from "src/app/_store/dig-lit-self-assessment/dig-lit-self-assessment.selectors";
import { takeUntil } from "rxjs/operators";
import { Observable, Subject } from "rxjs";
import {
  GetDigLitSelfAssessmentData,
  ResetDigLitSelfAssessmentState,
  SaveDigLitSelfAssessment,
  SaveDigLitSelfAssessmentSession,
  SetDigLitState,
} from "src/app/_store/dig-lit-self-assessment/dig-lit-self-assessment.actions";
import { HttpErrorResponse } from "@angular/common/http";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { ScormEntry } from "src/app/_models/ScormEntry";
import {
  ClearTriggerNavigation,
  SetDirtyForm,
} from "src/app/_store/navigation/navigation.actions";
import { selectAllowUnload } from "src/app/_store/navigation/navigation.selectors";
import { SelfAssessmentGuard } from "src/app/guards/self-assessment.guard";
import { IAppConfig } from "src/app/lib/config/app-config";
import { APP_CONFIG } from "src/app/lib/angular/injection-tokens";

@Component({
  selector: "app-digital-lit-self-assessment",
  templateUrl: "./digital-lit-self-assessment.component.html",
  styleUrls: ["./digital-lit-self-assessment.component.scss"],
})
export class DigitalLitSelfAssessmentComponent implements OnInit {
  @HostListener(AppConstants.WIN_BEFOREUNLOAD, [AppConstants.EVENT_PROP])
  unloadNotification($event: any): void {
    if (this.deactivateGuard.dirty && !this.allowUnload && !this.isCompleted) {
      $event.returnValue = true;
    }
  }

  @Input() courseIconPath: string;
  @Input() learnerId: number;
  @ViewChild(AppConstants.STEPPER_HORIZONTAL) stepperHorizontal: MatStepper;
  @ViewChild(AppConstants.STEPPER_VERICAL) stepperVertical: MatStepper;

  destroy$: Subject<void> = new Subject<void>();
  loader$: Observable<boolean>;
  allowUnload: boolean = false;
  result: DigLitSelfAssessmentResult;
  scormEntries: ScormEntry[] = [];
  siteLanguageCode: string;
  isWelsh: boolean;
  courseName: string;

  trigger: number = 0;
  currentStep: number = 0;
  userFirstName: string;
  userLastName: string;
  ready: boolean = false;
  isCompleted: boolean = false;
  lastStemWithAnswers: number;

  categoryDigitalResponsibility =
    DigLitQuestionCategories.DigitalResponsibility;
  categoryDigitalProductivity = DigLitQuestionCategories.DigitalProductivity;
  categoryInformationLiteracy = DigLitQuestionCategories.InformationLiteracy;
  categoryDigitalCollaboration = DigLitQuestionCategories.DigitalCollaboration;
  categoryDigitalCreativity = DigLitQuestionCategories.DigitalCreativity;
  questionModeMultiple = QuestionModes.Multiple;
  questionModeSlider = QuestionModes.Slider;
  welcomeENPath: string = DigLitSAConstants.DIG_LIT_SA_AUDIO_WELCOME;
  defaultQueue: string[] = [];
  forceMute: boolean = false;
  forcePause: string = AppConstants.BLANK;
  assetsPath: string = AppConstants.BLANK;
  audio: HTMLAudioElement | null = null;
  initialLoad: boolean = true;
  constructor(
    private cd: ChangeDetectorRef,
    private location: Location,
    private snackBar: MatSnackBar,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    public deactivateGuard: SelfAssessmentGuard,
    @Inject(LOCALE_ID) protected localeId: string,
    @Inject(APP_CONFIG) private config: IAppConfig
  ) {
    this.siteLanguageCode = localeId;

    if (this.config.scormContentLocation) {
      this.assetsPath = this.config.scormContentLocation;
    }
  }

  ngOnInit(): void {
    this.userFirstName = sessionStorage.getItem(AppConstants.FIRST_NAME);
    this.userLastName = sessionStorage.getItem(AppConstants.LAST_NAME);
    this.deactivateGuard.isComplete = false;
    this.deactivateGuard.dirty = false;
    this.ready = false;
    this.courseName = DigLitSAConstants.DIG_LIT_SA_COURSE_NAME;
    this.isWelsh =
      this.siteLanguageCode == AppConstants.WELSH_LANG_CODE ||
      this.siteLanguageCode == AppConstants.WELSH_GB_LANG_CODE;

    if (this.result != undefined)
      this.setStepperIndex(
        this.result.currentPage,
        this.result.lastStemWithAnswers
      );

    this.initializeStoreSelects();
  }

  ngAfterViewInit(): void {
    this.pipeHorizontalStepper();
    this.pipeVerticalStepper();
  }

  initializeStoreSelects(): void {
    this.loader$ = this.store.select(selectDigLitSelfAssessmentLoader);

    this.store
      .select(selectDigLitCurrentStep)
      .pipe(takeUntil(this.destroy$))
      .subscribe((currentPage: number) => {
        this.currentStep = currentPage;
      });

    this.store
      .select(selectDigLitSelfAssessmentDataState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: DigLitSelfAssessmentResult) => {
        if (!data) {
          return this.store.dispatch(
            GetDigLitSelfAssessmentData({ learnerId: this.learnerId })
          );
        }

        if (this.result == undefined) {
          this.result = JSON.parse(JSON.stringify(data));
          this.saveSession(this.result.currentPage);
          this.setStepperIndex(data.currentPage, data.lastStemWithAnswers);
        }

        this.ready = true;
      });

    this.store
      .select(selectSaveDigLitSelfAssessmentError)
      .pipe(takeUntil(this.destroy$))
      .subscribe((error: HttpErrorResponse) => {
        if (error && error.error) {
          this.snackBar.open(
            AppConstants.DIG_LIT_SELF_ASSESSMENT_SAVE_ERROR,
            null,
            {
              duration: 3000,
              horizontalPosition: AppConstants.CENTER,
              verticalPosition: AppConstants.BOTTOM,
              panelClass: [
                AppConstants.MAT_TOOLBAR,
                AppConstants.BG_ERROR,
                AppConstants.MAT_PRIMARY,
              ],
            }
          );
        }
      });

    this.store
      .select(selectSaveDigLitSelfAssessmentResult)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (result) {
          this.snackBar.open(
            AppConstants.DIG_LIT_SELF_ASSESSMENT_SAVE_SUCCESSFUL,
            null,
            {
              duration: 3000,
              horizontalPosition: AppConstants.CENTER,
              verticalPosition: AppConstants.BOTTOM,
              panelClass: [
                AppConstants.MAT_TOOLBAR,
                AppConstants.BG_SUCCESS,
                AppConstants.MAT_PRIMARY,
              ],
            }
          );

          this.navigateToLearningPage();
        }
      });

    this.store
      .select(selectAllowUnload)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allow: boolean) => {
        this.allowUnload = allow;
      });
  }

  pipeHorizontalStepper(): void {
    if (this.stepperHorizontal && this.stepperHorizontal.animationDone) {
      this.stepperHorizontal.animationDone.subscribe(() => {
        this.forceStep(this.stepperHorizontal, this.stepperVertical);
      });
    }
  }

  onSliderChange(event: Event): void {
    let target = event.target as HTMLInputElement;
    
    if (target && target.value) {
      this.playAudio(
        `${this.assetsPath}${getSliderAudio(Number(target.value), Voice.male)}`, true
      );
    }
  }

  pipeVerticalStepper(): void {
    if (this.stepperVertical && this.stepperVertical.animationDone) {
      this.stepperVertical.animationDone.subscribe(() => {
        this.forceStep(this.stepperVertical, this.stepperHorizontal);
      });
    }
  }

  forceStep(source: MatStepper, dest: MatStepper): void {
    this.trigger += 1;
    if (
      source.selectedIndex != dest.selectedIndex &&
      !(this.trigger % 2 === 0)
    ) {
      dest.selectedIndex = source.selectedIndex;
      this.cd.detectChanges();
    }
  }

  nextStep(dirty: boolean): void {
    if (this.stepperHorizontal) {
      this.stepperHorizontal.next();
    }
    if (this.stepperVertical) {
      this.stepperVertical.next();
    }

    if (dirty) {
      this.currentStep += 1;
      this.setDirtyState();
      this.saveSession(this.currentStep);
    }
  }

  prevStep(dirty: boolean): void {
    if (this.stepperHorizontal) {
      this.stepperHorizontal.previous();
    }
    if (this.stepperVertical) {
      this.stepperVertical.previous();
    }

    if (dirty) {
      this.currentStep -= 1;
      this.setDirtyState();
      this.saveSession(this.currentStep);
    }
  }

  navigateToLearningPage(): void {
    this.router.navigate([AppConstants.LEARNING_PAGE], {
      queryParamsHandling: AppConstants.MERGE,
      relativeTo: this.route,
    });
  }

  toggleCheckbox(
    qId: number,
    qType: string,
    answerId: number,
    value: any,
    isLastAnswer: boolean = false,
    stemId: number
  ): void {
    this.lastStemWithAnswers =
      stemId > this.lastStemWithAnswers ? stemId : this.lastStemWithAnswers;
    this.result.lastStemWithAnswers = this.lastStemWithAnswers;
    this.setDirtyState();
    this.updateDatasource(qId, qType, answerId, value, isLastAnswer);
    this.store.dispatch(
      SetDigLitState({ state: JSON.parse(JSON.stringify(this.result)) })
    );
  }

  toggleSlider(stemId: number): void {
    this.lastStemWithAnswers =
      stemId > this.lastStemWithAnswers ? stemId : this.lastStemWithAnswers;
    this.result.lastStemWithAnswers = this.lastStemWithAnswers;
    this.setDirtyState();
    this.store.dispatch(
      SetDigLitState({ state: JSON.parse(JSON.stringify(this.result)) })
    );
  }

  saveSession(currentStep: number): void {
    this.result.currentPage = currentStep;
    this.store.dispatch(
      SetDigLitState({ state: JSON.parse(JSON.stringify(this.result)) })
    );
    this.store.dispatch(
      SaveDigLitSelfAssessmentSession({
        data: {
          ...this.result,
          stems: this.formatStems(),
          currentPage: currentStep,
        },
      })
    );

    if (
      !this.initialLoad
    ) {
      this.playAudio(
        `${this.assetsPath}${this.getStemPath(this.result.stems[currentStep])}`
      );
    }

    if (this.initialLoad) {
      this.defaultQueue = [this.getStemPath(this.result.stems[currentStep])];
      this.initialLoad = false;
    }
  }

  submit(): void {
    this.isCompleted = true;
    this.deactivateGuard.isComplete = true;
    this.setDirtyState();
    this.store.dispatch(
      SaveDigLitSelfAssessment({
        data: { ...this.result, stems: this.formatStems() },
      })
    );
  }

  updateDatasource(
    qId: number,
    qType: string,
    answerId: number,
    value: any,
    isLastAnswer: boolean
  ): void {
    var newValue = null;
    this.result.stems.forEach((s) => {
      s.questions.forEach((q) => {
        if (
          q.id == qId &&
          q.type === QuestionModes.Multiple &&
          qType === QuestionModes.Multiple
        ) {
          newValue = value == null ? true : !value;
          q.answers.forEach((a) => {
            let disabled =
              isLastAnswer && a.id != answerId && newValue == true
                ? true
                : false;

            a.value =
              isLastAnswer && a.id != answerId
                ? false
                : a.id == answerId
                ? newValue
                : a.value;
            a.disabled = disabled;
            a.dirty = a.id == answerId ? true : a.dirty;
            a.description = this.formatTooltipIcon(a.description, disabled);
            a.descriptionCy = this.formatTooltipIcon(a.descriptionCy, disabled);
          });
        }
      });
    });
  }

  formatTooltipIcon(value: string, disabled: boolean): string {
    var newValue = disabled
      ? value.replace(
          AppConstants.DIG_LIT_INFO_ICON,
          AppConstants.DIG_LIT_INFO_ICON_DISABLED
        )
      : value.replace(
          AppConstants.DIG_LIT_INFO_ICON_DISABLED,
          AppConstants.DIG_LIT_INFO_ICON
        );

    return newValue;
  }

  setStepperIndex(currentStep: number, lastStemWithAnswers: number): void {
    this.lastStemWithAnswers = lastStemWithAnswers;
    var revertStepsCount = lastStemWithAnswers - currentStep;
    var stepCount =
      revertStepsCount > 0 ? lastStemWithAnswers - 1 : currentStep;

    setTimeout(() => {
      for (let i = 0; i < stepCount; i++) {
        this.nextStep(false);
      }
    }, 0);

    if (revertStepsCount > 0) {
      setTimeout(() => {
        for (let i = 1; i < revertStepsCount; i++) {
          this.prevStep(false);
        }
      }, 1);
    }
  }

  formatStems(): DigLitSelfAssessmentStem[] {
    return this.result.stems.map((stem) => {
      return {
        ...stem,
        questions: stem.questions.map((q) => {
          return {
            ...q,
            answers: q.answers.map((a) => {
              return {
                ...a,
                value: a.value != null ? String(a.value) : null,
                shortname: a.shortname.trim(),
              };
            }),
          };
        }),
      };
    });
  }

  formatLabel(value: number): string {
    return `${value}`;
  }

  setDirtyState(): void {
    this.deactivateGuard.dirty = true;
    this.store.dispatch(SetDirtyForm({ value: true }));
  }

  dispatchResetNavigationState(): void {
    this.store.dispatch(ClearTriggerNavigation());
  }

  dispatchResetState(): void {
    this.store.dispatch(ResetDigLitSelfAssessmentState());
  }

  cancelRedirect(): void {
    this.location.back();
  }

  clearDirtyState(): void {
    this.dispatchResetNavigationState();
    this.dispatchResetState();
    this.deactivateGuard.dirty = false;
  }

  playAudio(filePath?: string, slider = false): void {
    if (filePath != null) {
      if(this.audio) {
        this.audio.pause();
      }
      this.audio = new Audio(filePath);
      this.audio.muted = this.forceMute;
      this.audio.currentTime = slider ? 2 : 3;
      this.audio
        .play()
        .then(() => {
          this.forcePause = generateGUID();
        })
        .catch((error) => console.error(error));
    }
  }

  stopAudio(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
  }

  getQueue(answers: DigLitSelfAssessmentAnswers[]): string[] {
    return answers?.map((r) => this.isWelsh ? r.pathCy : r.path);
  }

  getStemPath(stem: DigLitSelfAssessmentStem): string {
    return this.isWelsh ? stem?.pathCy : stem?.path;
  }

  ngOnDestroy(): void {
    this.clearDirtyState();
    this.destroy$.next();
    this.destroy$.unsubscribe();

    this.forceMute = true;
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }
  }
}
